/**
 * The board protocol: JSON frames over one WebSocket. See docs/protocol.md.
 *
 * The board owns state, rules and order; the browser owns drawing and line editing.
 * Every frame the client sends gets exactly one frame back, possibly with no events.
 * The board may also push frames nobody asked for (lounge chatter); those carry `push: true`.
 */
import type { ScreenName, Skin } from "./skin";

export const PROTOCOL_VERSION = 5;

/**
 * Plain-text keepalive, outside the JSON frames. The client sends `ping` every
 * HEARTBEAT.everyMs; the runtime answers `pong` without waking the board.
 * Callers silent for two minutes are hung up so dead tabs don't hold nodes.
 */
export const HEARTBEAT = { ping: "ping", pong: "pong", everyMs: 25_000 } as const;

/** How the client should collect the next input. */
export type InputMode =
  | "key"     // one keypress, sent immediately
  | "line"    // a line, echoed
  | "secret"  // a line, masked
  | "body"    // a line of a message body, echoed
  | "chat"    // a lounge line; pushed lines arrive while it's being typed
  | "sealed"; // a vault line: the client encrypts it and speaks `vault` frames itself

/** Who and where the caller is, attached to every frame so the client can fill skin variables. */
export interface Ctx {
  node: number;
  handle?: string;
  area?: string;
}

export type Vars = Record<string, string | number>;

/** What a caller is doing, for the who's-on list. Labels come from the skin. */
export type Activity = "login" | "signup" | "main" | "reading" | "writing" | "duties" | "lounge" | "vault";

/**
 * One entry in the vault's log, in the order the board accepted it. `bytes` is a
 * base64 MLS message the board can't read. Who sent it, and who gained or lost a
 * key, is metadata the board does know.
 */
export interface VaultEntry {
  seq: number;
  /** The epoch the entry was made in. A commit moves the room from this epoch to the next. */
  epoch: number;
  kind: "commit" | "message";
  sender: string;
  added?: string;
  removed?: string;
  bytes: string;
  at: number;
}

export type VaultOp =
  /** Walk in. `since` is the last entry this device processed; `hasState` says whether it holds a key. */
  | { op: "enter"; since: number; hasState: boolean }
  /** Leave this device's public key package at the door. */
  | { op: "keypackage"; bytes: string }
  /** The sysop opens the vault for the first time. */
  | { op: "create" }
  /** The sysop starts over: every key and the whole log are dropped. For a sysop who lost their device. */
  | { op: "reopen" }
  | { op: "fetchKeyPackage"; handle: string }
  /** A commit built on `epoch`. Accepted only if that's still the current epoch. */
  | { op: "commit"; epoch: number; bytes: string; welcome?: string; added?: string; removed?: string }
  | { op: "send"; epoch: number; bytes: string }
  | { op: "leave" };

export type VaultEvent =
  | {
      t: "vault"; op: "state";
      exists: boolean; member: boolean; sysop: boolean; epoch: number;
      /** Whether the board holds a key package from this caller. */
      hasKeyPackage: boolean;
      /** A key waiting for this caller, and the log position it was handed at. */
      welcome: { bytes: string; seq: number } | null;
      backlog: VaultEntry[];
      members: string[];
      /** Handles with a key package at the door (sysop only). */
      waiting: string[];
    }
  | { t: "vault"; op: "keyPackage"; handle: string; bytes: string | null }
  | { t: "vault"; op: "entry"; entry: VaultEntry }
  | { t: "vault"; op: "accepted"; seq: number; epoch: number }
  | { t: "vault"; op: "rejected"; reason: "stale" | "notAllowed" | "noKeyPackage" | "notMember"; epoch: number }
  /** Someone left a key package at the door (sent to the sysop). */
  | { t: "vault"; op: "knocking"; handle: string };

export interface ChatLine {
  kind: "say" | "me" | "join" | "part" | "topic" | "kick" | "msg" | "roll" | "mute" | "unmute";
  handle: string;
  /** Member-written text for say, me, topic and msg; the result for roll. */
  text?: string;
  /** Who did it, for kick, mute and unmute. */
  by?: string;
  /** Who a private msg went to. Only the two of them ever see it. */
  to?: string;
  at: number;
}

export interface ListRow {
  id: number;
  author: string;
  subject: string;
  created_at: number;
  pending: boolean;
}

export interface MessageView {
  id: number;
  author: string;
  subject: string;
  body: string;
  created_at: number;
}

export type ServerEvent =
  /** First event on every connection. */
  | { t: "hello"; protocol: number; skin: Skin }
  | { t: "screen"; name: Exclude<ScreenName, "main">; vars?: Vars }
  /** The main menu. `locked` lines up with skin.areas; `reads` is null when reads are unlimited. */
  | {
      t: "menu"; level: number; locked: boolean[]; reads: number | null; pending: number | null; canSetLevels: boolean;
      /** null when posts are unlimited. */
      posts: number | null;
      /** null when the board has no lounge. */
      lounge: "open" | "locked" | null;
      /** null when the board has no vault; otherwise whether the caller holds a key. */
      vault: "member" | "door" | null;
    }
  /** One line of skin text. */
  | { t: "say"; key: string; vars?: Vars }
  /** Skin text that waits for input in the given mode. The last prompt in a frame is the live one. */
  | { t: "prompt"; key: string; vars?: Vars; input: InputMode; max: number }
  /** The board accepted a keypress. */
  | { t: "pressed"; key: string }
  | { t: "list"; rows: ListRow[] }
  | { t: "message"; message: MessageView }
  | { t: "who"; callers: { node: number; handle: string | null; activity: Activity }[] }
  /** One lounge line. */
  | { t: "chat"; line: ChatLine }
  /** Kept lounge lines, oldest first, shown on walking in. */
  | { t: "chatlog"; lines: ChatLine[] }
  /** Who's in the lounge. */
  | { t: "names"; people: { handle: string; op: boolean; away?: string }[] }
  | VaultEvent
  /** Text from somewhere other than the skin (the studio's builder), printed raw, never painted. */
  | { t: "text"; text: string }
  /** The board is hanging up after this frame. */
  | { t: "bye" };

export interface ServerFrame {
  v: typeof PROTOCOL_VERSION;
  ctx: Ctx;
  events: ServerEvent[];
  /** Set on frames the board sends unprompted. They don't answer the client's last input. */
  push?: true;
}

export type ClientFrame =
  | { v: typeof PROTOCOL_VERSION; t: "key"; key: string }
  | { v: typeof PROTOCOL_VERSION; t: "line"; text: string }
  | ({ v: typeof PROTOCOL_VERSION; t: "vault" } & VaultOp);
